import { useCallback, useState } from 'react';
import type { BlockRendererProps } from '@/lib/email-builder/block-registry';
import { parseBlockConfiguration } from '@/types/email-blocks';
import type { HtmlBlockConfiguration } from '@/types/block-configurations';

export function HtmlBlock({ block, isSelected, isHovered, onSelect, onHover }: BlockRendererProps) {
  const config = parseBlockConfiguration(block) as HtmlBlockConfiguration;
  const [frameHeight, setFrameHeight] = useState(48);

  // Size the frame to its content. Possible because of allow-same-origin; scripts stay disabled.
  const handleFrameLoad = useCallback((e: React.SyntheticEvent<HTMLIFrameElement>) => {
    const body = e.currentTarget.contentDocument?.body;
    if (body) {
      setFrameHeight(Math.max(body.scrollHeight, 24));
    }
  }, []);

  return (
    <div
      onClick={(e) => { e.stopPropagation(); onSelect(block.id); }}
      onMouseEnter={(e) => { e.stopPropagation(); onHover(block.id); }}
      onMouseLeave={() => onHover(null)}
      className={`
        lw-block-html cursor-pointer rounded transition-all
        ${isSelected ? 'ring-2 ring-primary ring-offset-2' : ''}
        ${isHovered && !isSelected ? 'ring-1 ring-muted-foreground/50' : ''}
      `}
    >
      {config.content ? (
        <div className="relative rounded border border-dashed border-border bg-muted/30 p-3">
          {/*
            User-authored HTML is rendered in a sandboxed frame, never in the app's own DOM:
            the sandbox has no allow-scripts, so <script> and inline handlers cannot run with
            the editor's session. The renderer inserts this markup into emails as-is.
          */}
          <iframe
            title="Custom HTML block preview"
            srcDoc={`<!doctype html><html><head><meta charset="utf-8"><style>body{margin:0}</style></head><body>${config.content}</body></html>`}
            sandbox="allow-same-origin"
            onLoad={handleFrameLoad}
            className="block w-full border-0"
            style={{ height: frameHeight }}
          />
          {/* Clicks inside an iframe don't reach the block wrapper; this overlay keeps selection working */}
          <div className="absolute inset-0" aria-hidden="true" />
        </div>
      ) : (
        <div className="flex min-h-16 items-center justify-center rounded border-2 border-dashed border-border bg-muted/20">
          <div className="text-center">
            <svg className="mx-auto mb-1 h-6 w-6 text-muted-foreground/50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
            </svg>
            <p className="text-sm text-muted-foreground select-none">Custom HTML Block</p>
          </div>
        </div>
      )}
    </div>
  );
}
